/**
 * Pure catalogue helpers for the Connectors screen.
 * Categories follow integration_providers.depth_category (analytics, code, revenue, ads).
 * MCP is its own tab. Write-capable detection is used by the connect dialog.
 */

import { formatRelative } from '../../core/format.js';

export const TABS = ['all', 'Ads', 'Revenue', 'Analytics', 'Code', 'MCP'];

const TAB_FROM_DEPTH = {
  ads: 'Ads',
  revenue: 'Revenue',
  platform: 'Revenue',
  analytics: 'Analytics',
  code: 'Code',
};

const DEPTH = [
  ['analytics', 'Analytics', 35],
  ['code', 'Code', 25],
  ['revenue', 'Revenue', 25],
  ['ads', 'Ads', 15],
];

const LIVE_HEALTH = new Set(['live', 'partial', 'expiring', 'healthy', 'unverified', 'quota_limited']);

/** Classic catalogue hides Connect on these until the provider is ready. */
export const COMING_SOON = new Set(['sharepoint', 'stripe', 'amazon-reports']);

const NATIVE_WRITE = new Set(['slack']);

/** Plan 4 §3 names these MCP servers, plus others whose tools create or send. */
const MCP_WRITE = new Set([
  'github', 'linear', 'notion', 'cloudflare', 'gitlab',
  'google-drive', 'slack', 'stripe', 'supabase',
]);

const WRITE_VERBS = new Set([
  'create', 'update', 'delete', 'write', 'post', 'send', 'edit', 'insert', 'patch', 'remove', 'publish',
]);

const AUTH_LABEL = {
  none: 'No auth',
  bearer: 'Bearer token',
  api_key: 'API key',
  oauth: 'OAuth',
};

export function catalogueCategory(item) {
  if (!item || item.kind === 'mcp') return 'MCP';
  const raw = String(item.depth_category || item.category || 'analytics').toLowerCase();
  return TAB_FROM_DEPTH[raw] || 'Analytics';
}

export function filterCatalogue(items, tab, query) {
  const q = String(query || '').trim().toLowerCase();
  return (items || []).filter((item) => {
    if (tab && tab !== 'all' && catalogueCategory(item) !== tab) return false;
    if (!q) return true;
    const blob = [item.title, item.name, item.description, item.meta, item.kindLabel]
      .filter(Boolean).join(' ').toLowerCase();
    return blob.includes(q);
  });
}

export function tabCounts(items) {
  const counts = { all: (items || []).length, Ads: 0, Revenue: 0, Analytics: 0, Code: 0, MCP: 0 };
  for (const item of items || []) {
    const cat = catalogueCategory(item);
    if (counts[cat] != null) counts[cat] += 1;
  }
  return counts;
}

export function toolCanWrite(tool) {
  if (!tool || typeof tool !== 'object') return false;
  const notes = tool.annotations || {};
  if (notes.readOnlyHint === true) return false;
  if (notes.readOnlyHint === false || notes.destructiveHint === true) return true;
  const words = `${tool.name || ''} ${tool.namespaced_name || ''}`.toLowerCase().split(/[^a-z]+/);
  return words.some((word) => WRITE_VERBS.has(word));
}

function slugOf(item) {
  return String((item && (item.name || item.provider_name || item.slug)) || '')
    .trim().toLowerCase().replace(/\s+/g, '-');
}

export function sourceCanWrite(item) {
  if (!item) return false;
  if (item.kind === 'mcp') {
    if (Array.isArray(item.tools) && item.tools.length) return item.tools.some(toolCanWrite);
    return MCP_WRITE.has(slugOf(item));
  }
  return NATIVE_WRITE.has(slugOf(item));
}

/** Last four characters, never a full secret and never a PEM block. */
export function keyHint(value) {
  const compact = String(value == null ? '' : value).replace(/\s+/g, '');
  if (compact.length < 8 || compact.includes('-----')) return '';
  return `••••${compact.slice(-4)}`;
}

export function isLive(row) {
  const health = String((row && row.health_status) || '').toLowerCase();
  if (!health) return true;
  return LIVE_HEALTH.has(health);
}

export function liveCount(rows) {
  return (rows || []).filter(isLive).length;
}

export function letterOf(name) {
  const text = String(name || '').trim();
  return text ? text.charAt(0).toUpperCase() : 'C';
}

export function readMeta(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function latencyWidth(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '0%';
  return `${Math.min(100, Math.max(8, Math.round((n / 500) * 100)))}%`;
}

export function depthParts(breakdown) {
  const src = breakdown || {};
  return DEPTH.map(([key, label, weight]) => {
    const got = Number(src[key]) || 0;
    const filled = got > 0;
    return {
      key,
      label,
      weight,
      filled,
      text: filled ? `${label} ${got}` : `${label} +${weight}`,
    };
  });
}

function ago(value, now) {
  const rel = formatRelative(value, now);
  if (rel === '—') return '';
  if (rel === 'now') return 'just now';
  if (/^\d+[mhd]$/.test(rel)) return `${rel} ago`;
  return rel;
}

export function liveTitle(row) {
  const label = String((row && row.label) || '').trim();
  return label || (row && (row.provider_display || row.provider_name)) || 'Connector';
}

export function liveSubtitle(row, now = Date.now()) {
  const meta = readMeta(row && row.metadata);
  const parts = [];
  if (meta.repo) parts.push(String(meta.repo));
  else if (Number(row && row.tool_count) > 0) {
    const n = Number(row.tool_count);
    parts.push(`${n} tool${n === 1 ? '' : 's'}`);
  }
  const checked = ago(row && row.last_tested_at, now);
  if (checked) parts.push(`checked ${checked}`);
  if (row && row.mode === 'synced') {
    const synced = ago(row.last_synced_at, now);
    parts.push(synced ? `synced ${synced}` : 'synced');
  }
  return parts.join(' · ') || 'Not checked yet';
}

export function healthLabel(row) {
  if (isLive(row)) return 'Live';
  return String((row && row.health_status) || 'check').replace(/_/g, ' ');
}

function nativeItem(provider, integrations) {
  const rows = (integrations || []).filter((row) => row.provider_id === provider.id);
  const category = catalogueCategory({ kind: 'native', depth_category: provider.depth_category, category: provider.category });
  const connected = rows.length > 0;
  const soon = !connected && COMING_SOON.has(provider.name);
  const title = provider.display_name || provider.name || 'Connector';
  return {
    kind: 'native',
    id: provider.id,
    name: provider.name,
    title,
    letter: letterOf(title),
    depth_category: provider.depth_category,
    category,
    kindLabel: 'Native',
    badge: soon ? 'Soon' : '',
    description: connected ? 'Connected for this product.' : `${category} source for this product.`,
    meta: `${category}${connected ? ' · live' : ''}`,
    connected,
    soon,
    instances: rows,
  };
}

function mcpItem(server) {
  const title = server.display_name || server.name || 'MCP server';
  const connected = server.connection_status === 'connected';
  const transport = String(server.transport || 'http').toUpperCase();
  const auth = AUTH_LABEL[server.auth_type] || server.auth_type || 'Bearer token';
  const tier = String(server.tier || '').toLowerCase();
  const badge = tier === 'sandboxed' ? 'Sandboxed' : (tier && tier !== 'blocked' ? 'Verified' : '');
  return {
    kind: 'mcp',
    id: server.id,
    name: server.name,
    title,
    letter: letterOf(title),
    category: 'MCP',
    kindLabel: 'MCP',
    badge,
    description: server.description || 'MCP server for this product.',
    meta: `${transport} · ${auth}`,
    connected,
    soon: false,
    auth_type: server.auth_type || 'bearer',
    transport: server.transport || 'http',
    connection_id: server.connection_id || '',
    connection_url: server.connection_url || '',
    registry_url: server.registry_url || '',
    tools: server.tools || null,
  };
}

export function assembleCatalogue(providers, integrations, servers) {
  const items = [];
  for (const provider of providers || []) {
    if (provider && provider.is_active === false) continue;
    if (provider) items.push(nativeItem(provider, integrations));
  }
  for (const server of servers || []) {
    if (server) items.push(mcpItem(server));
  }
  const order = { Ads: 0, Revenue: 1, Analytics: 2, Code: 3, MCP: 4 };
  items.sort((a, b) => (order[a.category] - order[b.category]) || a.title.localeCompare(b.title));
  return items;
}
